/**
 * useForgotPassword Composable
 *
 * Handles the "forgot password" flow: validates the email and calls the
 * backend endpoint that mails a password reset link. The backend always
 * returns a generic success message to avoid leaking which emails exist.
 *
 * @returns {Object} Email form state, loading/error/sent flags, and handleForgotPassword method
 */

import { ref } from 'vue'
import api from '@/services/api'
import { useToast } from '@/composables/ui/useToast'

export function useForgotPassword() {
  const { success: toastSuccess, error: toastError } = useToast()

  const email = ref('')
  const loading = ref(false)
  const error = ref('')
  const sent = ref(false)

  const handleForgotPassword = async (): Promise<boolean> => {
    try {
      loading.value = true
      error.value = ''
      sent.value = false

      if (!email.value) {
        throw new Error('Please enter your email address')
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(email.value)) {
        throw new Error('Please enter a valid email address')
      }

      await api.post('/auth/forgot-password', { email: email.value })

      sent.value = true
      toastSuccess('If that email is registered, a password reset link has been sent to it.', 6000)
      return true
    } catch (err: any) {
      let errorMessage = 'An error occurred. Please try again.'
      if (err.response?.data?.errors) {
        errorMessage = Object.values(err.response.data.errors).flat().join(', ')
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message
      } else if (err instanceof Error) {
        errorMessage = err.message
      }

      error.value = errorMessage
      toastError(errorMessage, 6000)
      return false
    } finally {
      loading.value = false
    }
  }

  const reset = () => {
    email.value = ''
    error.value = ''
    sent.value = false
  }

  return {
    email,
    loading,
    error,
    sent,
    handleForgotPassword,
    reset
  }
}
